import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Spinner,
  Text,
  Table,
} from "@chakra-ui/react";
import {
  CheckCircle,
  XCircle,
  Clock,
  Plane,
  RefreshCw,
  AlertTriangle,
  Trash2,
} from "lucide-react";
import {
  useGetQueueEndpointQuery,
  useGetListDronesEndpointQuery,
  usePostApproveEndpointMutation,
  usePostRevokeEndpointMutation,
  useDeleteForgetEndpointMutation,
} from "@/store";

export function ApprovalQueue() {
  const {
    data: queueData,
    isLoading: queueLoading,
    error: queueError,
    refetch: refetchQueue,
  } = useGetQueueEndpointQuery(undefined, {
    pollingInterval: 10000,
  });

  const {
    data: dronesData,
    isLoading: dronesLoading,
    error: dronesError,
    refetch: refetchDrones,
  } = useGetListDronesEndpointQuery({}, {
    pollingInterval: 10000,
  });

  const [approve, { isLoading: approving }] = usePostApproveEndpointMutation();
  const [revoke, { isLoading: revoking }] = usePostRevokeEndpointMutation();
  const [forget, { isLoading: forgetting }] = useDeleteForgetEndpointMutation();

  const pendingDrones = queueData?.drones ?? [];
  const allDrones = dronesData?.drones ?? [];
  const loading = queueLoading || dronesLoading;
  const error = queueError || dronesError;

  const handleRefresh = () => {
    refetchQueue();
    refetchDrones();
  };

  async function handleApprove(droneId: string) {
    try {
      await approve({
        droneId,
        body: { approvedBy: "admin" },
      }).unwrap();
    } catch {
      // Error is handled by RTK Query
    }
  }

  async function handleRevoke(droneId: string) {
    try {
      await revoke({
        droneId,
        body: { revokedBy: "admin", reason: "Revoked by admin" },
      }).unwrap();
    } catch {
      // Error is handled by RTK Query
    }
  }

  async function handleForget(droneId: string) {
    try {
      await forget({ droneId }).unwrap();
    } catch {
      // Error is handled by RTK Query
    }
  }

  function formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleString();
  }

  const approvedDrones = allDrones.filter((d) => d.status === "approved");
  const revokedDrones = allDrones.filter((d) => d.status === "revoked");

  const actionInProgress = approving || revoking || forgetting;

  return (
    <Box maxW="6xl" mx="auto">
      <Flex direction="column" gap="6">
        <Flex align="center" justify="space-between">
          <Box>
            <Heading as="h1" size="lg" display="flex" alignItems="center" gap="2">
              <Plane style={{ height: 24, width: 24 }} />
              Drone Approval
            </Heading>
            <Text fontSize="sm" color="fg.muted">
              Manage drone registration requests
            </Text>
          </Box>
          <Button variant="outline" onClick={handleRefresh} disabled={loading}>
            <RefreshCw
              style={{
                height: 16,
                width: 16,
                marginRight: 8,
                animation: loading ? "spin 1s linear infinite" : undefined,
              }}
            />
            Refresh
          </Button>
        </Flex>

        {error && (
          <Card.Root borderColor="red.500" bg="red.500/10">
            <Card.Body py="3">
              <Flex align="center" color="red.500">
                <AlertTriangle style={{ height: 16, width: 16, marginRight: 8 }} />
                {"status" in error ? `Error ${error.status}` : "Failed to fetch data"}
              </Flex>
            </Card.Body>
          </Card.Root>
        )}

        {loading && pendingDrones.length === 0 && allDrones.length === 0 ? (
          <Flex justify="center" py="12">
            <Spinner size="lg" />
          </Flex>
        ) : (
          <>
            <Card.Root bg="bg.subtle">
              <Card.Header>
                <Flex align="center" gap="2">
                  <Clock style={{ height: 20, width: 20 }} />
                  <Heading as="h2" size="md">
                    Pending Approval
                  </Heading>
                  <Badge colorPalette="orange">{pendingDrones.length}</Badge>
                </Flex>
              </Card.Header>
              <Card.Body pt="0">
                {pendingDrones.length === 0 ? (
                  <Text color="fg.muted" textAlign="center" py="4">
                    No drones pending approval
                  </Text>
                ) : (
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Drone ID</Table.ColumnHeader>
                        <Table.ColumnHeader>Model</Table.ColumnHeader>
                        <Table.ColumnHeader>Manufacturer</Table.ColumnHeader>
                        <Table.ColumnHeader>Registered</Table.ColumnHeader>
                        <Table.ColumnHeader textAlign="right">Actions</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {pendingDrones.map((drone) => (
                        <Table.Row key={drone.droneId}>
                          <Table.Cell fontFamily="mono" fontWeight="medium">
                            {drone.droneId}
                          </Table.Cell>
                          <Table.Cell>{drone.metadata?.model || "-"}</Table.Cell>
                          <Table.Cell>{drone.metadata?.manufacturer || "-"}</Table.Cell>
                          <Table.Cell fontSize="xs" color="fg.muted">
                            {formatDate(drone.createdAt)}
                          </Table.Cell>
                          <Table.Cell textAlign="right">
                            <Flex gap="2" justify="flex-end">
                              <Button
                                size="sm"
                                colorPalette="green"
                                onClick={() => handleApprove(drone.droneId)}
                                disabled={actionInProgress}
                              >
                                {approving ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <CheckCircle style={{ height: 14, width: 14, marginRight: 4 }} />
                                )}
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                colorPalette="red"
                                variant="outline"
                                onClick={() => handleRevoke(drone.droneId)}
                                disabled={actionInProgress}
                              >
                                <XCircle style={{ height: 14, width: 14, marginRight: 4 }} />
                                Reject
                              </Button>
                            </Flex>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                )}
              </Card.Body>
            </Card.Root>

            <Card.Root bg="bg.subtle">
              <Card.Header>
                <Flex align="center" gap="2">
                  <CheckCircle style={{ height: 20, width: 20, color: "var(--chakra-colors-green-500)" }} />
                  <Heading as="h2" size="md">
                    Approved Drones
                  </Heading>
                  <Badge colorPalette="green">{approvedDrones.length}</Badge>
                </Flex>
              </Card.Header>
              <Card.Body pt="0">
                {approvedDrones.length === 0 ? (
                  <Text color="fg.muted" textAlign="center" py="4">
                    No approved drones
                  </Text>
                ) : (
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Drone ID</Table.ColumnHeader>
                        <Table.ColumnHeader>Model</Table.ColumnHeader>
                        <Table.ColumnHeader>Manufacturer</Table.ColumnHeader>
                        <Table.ColumnHeader>Registered</Table.ColumnHeader>
                        <Table.ColumnHeader textAlign="right">Actions</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {approvedDrones.map((drone) => (
                        <Table.Row key={drone.droneId}>
                          <Table.Cell fontFamily="mono" fontWeight="medium">
                            {drone.droneId}
                          </Table.Cell>
                          <Table.Cell>{drone.metadata?.model || "-"}</Table.Cell>
                          <Table.Cell>{drone.metadata?.manufacturer || "-"}</Table.Cell>
                          <Table.Cell fontSize="xs" color="fg.muted">
                            {formatDate(drone.createdAt)}
                          </Table.Cell>
                          <Table.Cell textAlign="right">
                            <Button
                              size="sm"
                              colorPalette="red"
                              variant="outline"
                              onClick={() => handleRevoke(drone.droneId)}
                              disabled={actionInProgress}
                            >
                              {revoking ? (
                                <Spinner size="sm" />
                              ) : (
                                <XCircle style={{ height: 14, width: 14, marginRight: 4 }} />
                              )}
                              Revoke
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                )}
              </Card.Body>
            </Card.Root>

            {revokedDrones.length > 0 && (
              <Card.Root bg="bg.subtle" opacity="0.7">
                <Card.Header>
                  <Flex align="center" gap="2">
                    <XCircle style={{ height: 20, width: 20, color: "var(--chakra-colors-red-500)" }} />
                    <Heading as="h2" size="md">
                      Revoked Drones
                    </Heading>
                    <Badge colorPalette="red">{revokedDrones.length}</Badge>
                  </Flex>
                </Card.Header>
                <Card.Body pt="0">
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Drone ID</Table.ColumnHeader>
                        <Table.ColumnHeader>Model</Table.ColumnHeader>
                        <Table.ColumnHeader>Manufacturer</Table.ColumnHeader>
                        <Table.ColumnHeader>Registered</Table.ColumnHeader>
                        <Table.ColumnHeader textAlign="right">Actions</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {revokedDrones.map((drone) => (
                        <Table.Row key={drone.droneId}>
                          <Table.Cell fontFamily="mono" fontWeight="medium" color="fg.muted">
                            {drone.droneId}
                          </Table.Cell>
                          <Table.Cell color="fg.muted">{drone.metadata?.model || "-"}</Table.Cell>
                          <Table.Cell color="fg.muted">{drone.metadata?.manufacturer || "-"}</Table.Cell>
                          <Table.Cell fontSize="xs" color="fg.muted">
                            {formatDate(drone.createdAt)}
                          </Table.Cell>
                          <Table.Cell textAlign="right">
                            <Button
                              size="sm"
                              colorPalette="gray"
                              variant="outline"
                              onClick={() => handleForget(drone.droneId)}
                              disabled={actionInProgress}
                            >
                              {forgetting ? (
                                <Spinner size="sm" />
                              ) : (
                                <Trash2 style={{ height: 14, width: 14, marginRight: 4 }} />
                              )}
                              Forget
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Card.Body>
              </Card.Root>
            )}
          </>
        )}
      </Flex>
    </Box>
  );
}
